// src/auth/cognitoService.js
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute
} from 'amazon-cognito-identity-js';

const poolData = {
  UserPoolId: 'us-east-2_9ZjxKZJ4v',      // your User Pool ID
  ClientId: '7s54jcehmo01qvon7t0ujdtaag'   // your App Client ID
};

const userPool = new CognitoUserPool(poolData);


/**
 * Sign up a new user
 * @param {Object} payload - User registration data
 * @param {string} payload.email - User's email address
 * @param {string} payload.password - User's password
 * @param {string} payload.given_name - User's first name
 * @param {string} payload.family_name - User's last name
 * @param {string} [payload.phone_number] - User's phone number (optional, must be E.164 if used)
 * @param {string} [payload.role] - User's role (optional, custom attribute)
 * @param {string} [payload.practice_id] - Practice ID (optional, custom attribute)
 * @returns {Promise<Object>} - Resolves with a structured signup result or rejects with Cognito error
 */
export function signUp(payload) {
  const {
    email,
    password,
    given_name,
    family_name,
    phone_number,
    role,
    practice_id
  } = payload;

  

  // Validate required fields
  if (!email || !given_name || !family_name) {
    const error = new Error('Missing required fields: email, password, given_name, family_name');
    console.error('❌ Validation error:', error);
    return Promise.reject(error);
  }

  // Always lowercase the email for consistency
  const normalizedEmail = email.trim().toLowerCase();
  const username = normalizedEmail;


  // Build the array of CognitoUserAttribute instances
  const userAttributes = [
    new CognitoUserAttribute({ Name: 'email',       Value: normalizedEmail }),
    new CognitoUserAttribute({ Name: 'given_name',  Value: given_name.trim() }),
    new CognitoUserAttribute({ Name: 'family_name', Value: family_name.trim() }),
    new CognitoUserAttribute({ Name: 'custom:user_role', Value: role }),
  ];

  // Only push phone_number if provided (and in E.164 format)
  // if (phone_number) {
  //   const trimmedPhone = phone_number.trim();
    
  //   // Basic E.164 validation
  //   if (!trimmedPhone.startsWith('+')) {
  //     console.warn('⚠️ Phone number should start with + for E.164 format');
  //   }
    
  //   userAttributes.push(
  //     new CognitoUserAttribute({ Name: 'phone_number', Value: trimmedPhone })
  //   );
  // }

  // Custom attributes must be prefixed with "custom:"
  // if (role) {
  //   userAttributes.push(
  //     new CognitoUserAttribute({ Name: 'custom:role', Value: role.trim() })
  //   );
  // }

  if (practice_id) {
    userAttributes.push(
      
      new CognitoUserAttribute({ Name: 'custom:id_practice', Value: practice_id.trim() })
    );
  }



  return new Promise((resolve, reject) => {
    userPool.signUp(
      username,
      password,
      userAttributes,
      /* validationData */ [],
      (err, result) => {
        
        if (err) {
          console.error('❌ Signup error details:', {
            name: err.name,
            message: err.message,
            code: err.code,
            statusCode: err.statusCode,
            stack: err.stack
          });
          
          // Check if user already exists
          if (err.code === 'UsernameExistsException') {
            console.error('❌ User already exists with email:', normalizedEmail);
            const userExistsError = new Error('An account with this email already exists. Please sign in instead.');
            userExistsError.code = 'UsernameExistsException';
            userExistsError.userExists = true;
            userExistsError.email = normalizedEmail;
            return reject(userExistsError);
          }
          
          // Check for invalid email format
          if (err.code === 'InvalidParameterException') {
            console.error('❌ Invalid parameter:', err.message);
            const invalidParamError = new Error(err.message);
            invalidParamError.code = 'InvalidParameterException';
            return reject(invalidParamError);
          }
          
          return reject(err);
        }


        // Structure the data you care about
        resolve({
          user: result.user,
          userSub: result.userSub,
          userConfirmed: result.userConfirmed,
          codeDeliveryDetails: result.codeDeliveryDetails
        });
      }
    );
  });
}

/**
 * Sign in a user
 * @param {Object} credentials - Login credentials
 * @param {string} credentials.username - Username (email)
 * @param {string} credentials.password - Password
 * @returns {Promise} - Promise that resolves with the session
 */
export function signIn({ username, password }) {
  
  const user = new CognitoUser({ Username: username, Pool: userPool });
  const authDetails = new AuthenticationDetails({ Username: username, Password: password });

  return new Promise((resolve, reject) => {
    user.authenticateUser(authDetails, {
      onSuccess: (session) => {
        
        // Store token and user info
        localStorage.setItem('token', session.getAccessToken().getJwtToken());
        localStorage.setItem('user', JSON.stringify({
          username: session.getIdToken().payload.email,
          email: session.getIdToken().payload.email,
          given_name: session.getIdToken().payload.given_name,
          family_name: session.getIdToken().payload.family_name,
          sub: session.getIdToken().payload.sub
        }));
        
        resolve(session);
      },
      onFailure: (err) => {
        console.error('❌ Sign in error:', err);
        reject(err);
      },
      newPasswordRequired: (userAttributes, requiredAttributes) => {
        reject(new Error('New password required'));
      }
    });
  });
}

/**
 * Confirm user registration with verification code
 * @param {string} username - Username (email)
 * @param {string} code - Verification code
 * @returns {Promise} - Promise that resolves when confirmed
 */
export function confirmSignUp(username, code) {
  
  const user = new CognitoUser({ Username: username, Pool: userPool });
  
  return new Promise((resolve, reject) => {
    user.confirmRegistration(code, true, (err, result) => {
      if (err) {
        console.error('❌ Confirmation error:', err);
        return reject(err);
      }
      
      resolve(result);
    });
  });
}

/**
 * Resend confirmation code
 * @param {string} username - Username (email)
 * @returns {Promise} - Promise that resolves when code is sent
 */
export function resendConfirmationCode(username) {
  const user = new CognitoUser({ Username: username, Pool: userPool });
  
  return new Promise((resolve, reject) => {
    user.resendConfirmationCode((err, result) => {
      if (err) {
        console.error('❌ Resend code error:', err);
        return reject(err);
      }
      
      resolve(result);
    });
  });
}

/**
 * Get current authenticated user (if any)
 * @returns {CognitoUser|null} - The current authenticated user or null
 */
export function getCurrentUser() {
  return userPool.getCurrentUser();
}

/**
 * Get current user session
 * @returns {Promise} - Promise that resolves with the current session
 */
export function getCurrentSession() {
  const user = getCurrentUser();
  
  if (!user) {
    return Promise.reject(new Error('No current user'));
  }

  return new Promise((resolve, reject) => {
    user.getSession((err, session) => {
      if (err) {
        return reject(err);
      }
      
      if (!session.isValid()) {
        return reject(new Error('Session is not valid'));
      }
      
      resolve(session);
    });
  });
}

/**
 * Signs out the current user locally.
 */
export function signOut() {
  const user = userPool.getCurrentUser();
  if (!user) {
    console.warn('No Cognito user signed in to sign out 😕');
    return;
  }
  
  user.signOut();

  // Clear stored data
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  
}

/**
 * Global sign out (signs out from all devices)
 */
export function globalSignOut() {
  const user = getCurrentUser();
  if (!user) {
    console.warn('No Cognito user signed in to sign out 😕');
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    user.getSession((err, session) => {
      if (err) {
        return reject(err);
      }

      user.globalSignOut({
        onSuccess: () => {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          resolve();
        },
        onFailure: (err) => {
          console.error('❌ Global sign out error:', err);
          reject(err);
        }
      });
    });
  });
}

/**
 * Forgot password - initiate password reset
 * @param {string} username - Username (email)
 * @returns {Promise} - Promise that resolves when reset code is sent
 */
export function forgotPassword(username) {
  const user = new CognitoUser({ Username: username, Pool: userPool });
  
  return new Promise((resolve, reject) => {
    user.forgotPassword({
      onSuccess: (result) => {
        resolve(result);
      },
      onFailure: (err) => {
        console.error('❌ Forgot password error:', err);
        reject(err);
      }
    });
  });
}

/**
 * Confirm forgot password with new password and verification code
 * @param {string} username - Username (email)
 * @param {string} code - Verification code
 * @param {string} newPassword - New password
 * @returns {Promise} - Promise that resolves when password is reset
 */
export function confirmPassword(username, code, newPassword) {
  const user = new CognitoUser({ Username: username, Pool: userPool });
  
  return new Promise((resolve, reject) => {
    user.confirmPassword(code, newPassword, {
      onSuccess: () => {
        resolve();
      },
      onFailure: (err) => {
        console.error('❌ Password reset error:', err);
        reject(err);
      }
    });
  });
}
